import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { Auth } from "aws-amplify";
import { setCreds, setManagerInfo, signInUser } from "../../Features/userSlice";
import { rememberPropertyLink, forgetPropertyLink } from "../../Helper/propertyLink";

// Set REACT_APP_PROPERTY_LINK_API_URL=http://localhost:4000 to open property links against a local engine.
const API = process.env.REACT_APP_PROPERTY_LINK_API_URL || "https://api.linkbase.ca";
const apiHeaders = {
  "Content-Type": "application/json",
  "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
};

const ownerImageUrl = (key) => {
  if (!key) return null;
  if (key.startsWith("http")) return key;
  return `https://dl6ri8ttf37m3.cloudfront.net/public/${encodeURIComponent(key)}?width=600&format=webp`;
};

async function signInAsHost(email, encryptedPassword) {
  try {
    const current = await Auth.currentAuthenticatedUser();
    if (current?.attributes?.email?.toLowerCase() === email?.toLowerCase()) return;
    await Auth.signOut();
  } catch {
    // No session yet.
  }
  const { data } = await axios.post(`${API}/auth/decrypt-password`, { encryptedPassword }, { headers: apiHeaders });
  if (!data?.data?._p) throw new Error("Decryption returned empty password");
  await Auth.signIn(email, atob(data.data._p));
}

/**
 * `/p/:roomId`: the permanent guest link for a manual-account property. There
 * is no booking, so this loads the property, signs in the same way /go does,
 * and opens the main screen in property-only mode.
 */
export const PropertyLanding = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !roomId) return;
    started.current = true;

    (async () => {
      try {
        const { data } = await axios.get(`${API}/rooms/${encodeURIComponent(roomId)}/guest-bootstrap`, {
          headers: apiHeaders,
          params: { _ts: Date.now() },
        });
        const { room, owner } = data.data;

        const manager = {
          name: owner.name,
          email: owner.email,
          images: ownerImageUrl(owner.images),
          stripeId: owner.stripeId,
          password: owner.password,
          accountMetas: owner.accountMetas || null,
          extendedMetas: owner.extendedMetas || null,
        };
        dispatch(setCreds({ email: owner.email, password: owner.password, stripeId: owner.stripeId }));
        dispatch(setManagerInfo(manager));

        await signInAsHost(owner.email, owner.password);
        dispatch(signInUser());

        rememberPropertyLink(roomId);
        sessionStorage.setItem("isV2", "true");

        navigate("/main-v2", {
          replace: true,
          state: {
            room: {
              ...room,
              owner: { id: owner.id, timezone: room.timezone || owner.timezone || "America/Los_Angeles" },
            },
            // Stands in for a booking: no stay, no security steps, nothing to unlock.
            booking: {
              id: null,
              propertyOnly: true,
              roomID: room.id,
              owner: room.owner,
              securityStepsComplete: true,
              guestApp: { showLockButton: false },
            },
            manager,
          },
        });
      } catch (err) {
        console.error("Property link failed:", err);
        // A link that's gone must not keep being reopened on every reload.
        if (err?.response?.status === 404) forgetPropertyLink();
        setError(
          err?.response?.status === 404
            ? "This property link isn't available."
            : "We couldn't open this property. Please try again."
        );
      }
    })();
  }, [roomId, navigate, dispatch]);

  return (
    <Flex h="100vh" direction="column" alignItems="center" justifyContent="center" gap={3} px={6}>
      {error ? (
        <Text fontSize="14px" color="gray.300" textAlign="center">{error}</Text>
      ) : (
        <CircularProgress isIndeterminate color="#817EFF" size="40px" trackColor="transparent" />
      )}
    </Flex>
  );
};
